<template>
  <div v-if="!getLoadingBoolean" class="listing-view">
    <template v-if="!settingFilters">
      <h2>{{ getBusinessName }}</h2>
      <p>{{ $t('message.reviews.reviewsMsg') }}</p>
      <div class="row mx-n2 mt-1">
        <div class="col-12 mb-3 px-2 average-score-container" data-cy="rating-card">
          <AverageScoreCard
            :average="
              isDepartmentBreakdown
                ? parseFloat(businessReviewRatings[`${chosenDepartmentFilter}`]['rating'])
                : businessReviewOverallRating
            "
            :reviewCount="
              isDepartmentBreakdown
                ? businessReviewRatings[`${chosenDepartmentFilter}`]['reviewCount']
                : businessReviewOverallRatingCount
            "
            :label="averageScoreCardLabel"
          />
        </div>
        <div class="col mb-3 px-2 breakdown-card-container">
          <BreakdownCard v-if="!isGmbReviews" :scores="businessReviewAggregateScores" />
          <DepartmentBreakdownCard
            v-else
            :scores="businessReviewRatings"
            :departmentBreakdown="this.$store.state.reviews.departmentBreakdown"
            :isDepartmentBreakdown="isDepartmentBreakdown"
            :totalReviewCount="businessReviewRatings[`${chosenDepartmentFilter}`]['reviewCount']"
          />
        </div>
      </div>
      <MobialisButton v-if="filterButton" @click="showFilters" variant="secondary" class="mb-3">
        {{ $t('message.reviews.filterLabel') }}
      </MobialisButton>
      <div v-if="!filterButton" class="mb-1">
        <div class="row form-row">
          <div v-if="multipleDepartments" class="col-sm-3">
            <mobialis-selection-input-with-validation
              :label="`${$t('message.reviews.department')}`"
              vid="department"
              :options="filteredDepartmentOptions"
              v-model="chosenDepartmentFilter"
            >
            </mobialis-selection-input-with-validation>
          </div>
          <div v-if="!isGmbReviews" class="col-sm-3">
            <mobialis-selection-input-with-validation
              :label="`${$t('message.reviews.platform')}`"
              vid="platform"
              :options="filteredPlatformOptions"
              v-model="chosenPlatformFilter"
            >
            </mobialis-selection-input-with-validation>
          </div>
          <div class="col-sm-3">
            <mobialis-selection-input-with-validation
              :label="`${$t('message.reviews.sort')}`"
              vid="sorting"
              :options="sortingOptions"
              v-model="chosenSorting"
            >
            </mobialis-selection-input-with-validation>
          </div>
        </div>
      </div>
      <div class="mb-5">
        <template v-for="(review, index) in getDisplayedReviews">
          <ReviewCard
            v-if="review"
            :key="index"
            :review="review"
            :onlyOneDepartment="onlyOneDepartment"
            :business="getBusinessName"
          >
          </ReviewCard>
          <CtaCard
            v-else
            :key="`cta-${index}`"
            :product="returnCtaProduct(index, !getStillMoreReviews, getAdSlots, getDisplayedReviews.length)"
            :buttonLabel="`${$t('message.reviews.ads.buttonLabel')}`"
            :horizontal="!isAVRMobile"
          >
          </CtaCard>
        </template>
        <MobialisButton v-if="getStillMoreReviews" @click="loadMore" class="mt-1 mb-3" data-cy="load-more-button">
          <div v-if="!getLoadingReviewsBoolean">{{ $t('message.reviews.readMore') }}</div>
          <div v-else class="spinner-border spinner-border-sm"></div>
        </MobialisButton>
      </div>
    </template>
    <!-- Filter Panel for mobile -->
    <template v-if="settingFilters">
      <h2>{{ $t('message.reviews.filterLabel') }}</h2>
      <h4 v-if="!isGmbReviews">{{ $t('message.reviews.byPlatform') }}</h4>
      <MobialisFancyRadio
        v-if="!isGmbReviews"
        class="py-1"
        v-model="chosenPlatformFilter"
        name="platformFilter"
        :textArray="filteredPlatformOptions"
      >
      </MobialisFancyRadio>
      <mobialis-selection-input-with-validation
        :label="`${$t('message.reviews.department')}`"
        v-if="isGmbReviews"
        vid="department"
        :options="filteredDepartmentOptions"
        v-model="chosenDepartmentFilter"
      >
      </mobialis-selection-input-with-validation>
      <MobialisSelectionInputWithValidation
        class="pb-3"
        label="Sort"
        vid="sorting"
        :options="sortingOptions"
        v-model="chosenSorting"
      >
      </MobialisSelectionInputWithValidation>
      <MobialisButton @click="setFilters" class="my-2">{{ $t('message.reviews.applyButtonLabel') }}</MobialisButton>
      <MobialisButton @click="cancelFilters" variant="secondary" class="my-2">{{
        $t('message.common.cancel')
      }}</MobialisButton>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import { ctaChoiceMixin } from '@/mixins';
import AverageScoreCard from '@/components/reviews/AverageScoreCard.vue';
import BreakdownCard from '@/components/reviews/BreakdownCard.vue';
import DepartmentBreakdownCard from '@/components/reviews/DepartmentBreakdownCard.vue';
import ReviewCard from '@/components/reviews/ReviewCard.vue';
import CtaCard from '@/components/reviews/CtaCard.vue';
import log from '@/services/log';

export default {
  name: 'Listing',
  components: {
    AverageScoreCard,
    BreakdownCard,
    DepartmentBreakdownCard,
    ReviewCard,
    CtaCard,
  },
  mixins: [ctaChoiceMixin],
  data() {
    return {
      settingFilters: false, // bool to hide and show filter page
      unwatch: null, // store the function that will remove the watcher
      currentFilters: {}, // save filters to undo changes on cancel
    };
  },
  methods: {
    ...mapActions(['goHome']),
    ...mapActions('reviews', ['fetchReviews', 'reset', 'replaceFilters', 'loadMore', 'ctaProduct']),
    showFilters() {
      this.settingFilters = true;
      this.currentFilters = cloneDeep(this.activeFilters); // save in case of cancel
    },
    cancelFilters() {
      this.replaceFilters(this.currentFilters);
      this.settingFilters = false;
    },
    setFilters() {
      this.$store.commit('SET_LOADING', true);
      // new filters mean a new set of reviews. clear displayedReviews, and fetch again
      this.reset();
      try {
        this.fetchReviews();
        this.$store.commit('SET_LOADING', false);
        this.settingFilters = false;
      } catch (err) {
        log('Error fetching reviews in setFilters inside mobile/reviewsListing', err);
      }
    },
  },
  computed: {
    ...mapGetters(['getLoadingBoolean']),
    ...mapGetters('reviews', [
      'getDisplayedReviews',
      'getActiveFilters',
      'getAdSlots',
      'getStillMoreReviews',
      'getLoadingReviewsBoolean',
      'businessReviewOverallRating',
      'businessReviewOverallRatingCount',
      'businessReviewAggregateScores',
      'isGmbReviews',
      'businessReviewRatings',
    ]),
    ...mapGetters('business', ['getBusinessName', 'getSubscribedProducts']),
    ...mapState('reviews', ['displayedReviews', 'activeFilters']),
    averageScoreCardLabel() {
      if (this.isGmbReviews) {
        // Show the department label if we are showing the department breakdown and there is more then one department
        if (this.isDepartmentBreakdown && this.chosenDepartmentFilter !== 'overview' && this.multipleDepartments) {
          return this.$t(`message.reviews.${this.chosenDepartmentFilter}Rating`);
        }
        return this.$t('message.reviews.dealershipRating');
      }
      return this.$t('message.reviews.averageScore');
    },
    multipleDepartments() {
      return this.isGmbReviews && this.filteredDepartmentOptions.length > 2;
    },
    onlyOneDepartment() {
      return this.isGmbReviews && this.filteredDepartmentOptions.length === 2;
    },
    filterButton() {
      return (
        (this.isAVRMobile && !this.isGmbReviews) || (this.isAVRMobile && this.isGmbReviews && this.multipleDepartments)
      );
    },
    isDepartmentBreakdown() {
      // Show star breakdown when only one gmb department or when you select a department
      return this.isGmbReviews && (this.getActiveFilters.department !== 'overview' || !this.multipleDepartments);
    },
    // Non-Gmb reviews only
    chosenPlatformFilter: {
      get() {
        return this.getActiveFilters.platform;
      },
      set(newValue) {
        if (newValue !== this.getActiveFilters.platform) {
          this.$store.dispatch('reviews/updateActivePlatformFilter', newValue);
          if (!this.filterButton) {
            this.setFilters();
          }
        }
      },
    },
    chosenSorting: {
      get() {
        if (this.isGmbReviews) {
          return this.getActiveFilters.gmbSorting;
        }
        return this.getActiveFilters.sorting;
      },
      set(newValue) {
        if (newValue !== this.getActiveFilters.sorting && newValue !== this.getActiveFilters.gmbSorting) {
          if (this.isGmbReviews) {
            this.$store.dispatch('reviews/updateActiveGmbSortingFilter', newValue);
          } else {
            this.$store.dispatch('reviews/updateActiveSortingFilter', newValue);
          }
          if (!this.filterButton) {
            this.setFilters();
          }
        }
      },
    },
    // Gmb reviews only
    chosenDepartmentFilter: {
      get() {
        return this.getActiveFilters.department;
      },
      set(newValue) {
        if (newValue !== this.getActiveFilters.department) {
          this.$store.dispatch('reviews/updateActiveDepartmentFilter', newValue);
          if (!this.filterButton) {
            this.setFilters();
          }
        }
      },
    },
    // Non-gmb reviews only
    filteredPlatformOptions() {
      // for each set of platform options, check if reviews platform number (platformOption.value) is found (.some) in an array that only includes the platforms with more than 0 reviews.
      // That way only the filter buttons required are actually rendered.
      return this.$t('message.reviews.platformFilter').filter(platformOption =>
        this.businessReviewAggregateScores
          .filter(platforms => platforms.number_of_reviews > 0)
          .some(filteredPlatforms => platformOption.value === filteredPlatforms.outlet || platformOption.value === '0')
      );
    },
    // Gmb reviews only
    filteredDepartmentOptions() {
      return this.$t('message.reviews.departmentFilter').filter(
        item => this.businessReviewRatings[`${item.value.toLowerCase()}`] || item.value === 'overview'
      );
    },
    sortingOptions() {
      return this.isGmbReviews ? this.$t('message.reviews.gmbSortingFilter') : this.$t('message.reviews.sortingFilter');
    },
  },
  created() {
    // Gmb reviews only
    // If we only have one department then select it
    if (this.filteredDepartmentOptions.length === 2) {
      const array = this.filteredDepartmentOptions.filter(item => item.value !== 'overview');
      this.$store.dispatch('reviews/updateActiveDepartmentFilter', array[0].value);
      this.setFilters();
    }
  },
  mounted() {
    if (this.displayedReviews.length > 0) {
      this.$store.commit('SET_LOADING', false);
    } else {
      this.unwatch = this.$store.watch(
        state => state.reviews.fetchingFinished,
        () => {
          this.$store.commit('SET_LOADING', false);
          this.unwatch();
        }
      );
    }
    // vuex watcher required as normal watcher watching a getter or mapped state didn't work
  },
};
</script>

<style scoped lang="scss">
.listing-view {
  margin: 20px 0 40px;
}
.card {
  -webkit-box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
}
.average-score-container,
.breakdown-card-container {
  width: 100%;
  .card {
    height: 100%;
  }
}
@media (min-width: 576px) {
  .average-score-container {
    -webkit-box-flex: 0;
    -ms-flex: 0 0 37%;
    flex: 0 0 37%;
    max-width: 37%;
  }
}
</style>
